import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Package as PackageIcon, GripVertical } from 'lucide-react';
import AppLayout from '../components/AppLayout';
import Drawer from '../components/Drawer';
import PackageFormSlider from '../components/PackageFormSlider';
import PackageDetailsSlider from '../components/PackageDetailsSlider';
import api from '../services/api';
import '../styles/Builders.css';
import '../styles/Buttons.css';
import '../styles/DataTable.css';

interface Package {
  _id: string;
  name: string;
  description?: string;
  caption?: string;
  projectId: string;
  projectName?: string;
  location?: string;
  phase?: string;
  isActive: boolean;
  order: number;
  labourCost?: number;
  materialCost?: number;
  heroImageId?: string;
  heroImageUrl?: string;
  collections?: any[];
  includedOptions?: any[];
  createdAt: string;
  updatedAt: string;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

interface Project {
  _id: string;
  name: string;
}

const Packages: React.FC = () => {
  const navigate = useNavigate();
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const [packages, setPackages] = useState<Package[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  const [formLoading, setFormLoading] = useState(false);

  // Drag and drop state
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // Set default project filter to first project
  useEffect(() => {
    if (workspaceId) {
      fetchProjects();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  // Set first project as default when projects are loaded
  useEffect(() => {
    if (projects.length > 0 && !projectFilter) {
      setProjectFilter(projects[0]._id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects]);

  useEffect(() => {
    if (workspaceId && projectFilter) {
      fetchPackages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, pagination.page, searchQuery, projectFilter]);

  const fetchProjects = async () => {
    if (!workspaceId) return;

    try {
      const response = await api.get(`/workspaces/${workspaceId}/projects`, {
        params: {
          limit: 1000,
          sortBy: 'name',
          sortOrder: 'asc'
        }
      });
      setProjects(response.data.projects);
    } catch (err: any) {
      console.error('Error fetching projects:', err);
    }
  };

  const fetchPackages = async () => {
    if (!workspaceId) return;

    try {
      setLoading(true);
      setError('');

      const response = await api.get(`/workspaces/${workspaceId}/pkg-packages`, {
        params: {
          page: pagination.page,
          limit: pagination.limit,
          search: searchQuery,
          projectId: projectFilter || undefined,
          sortBy: 'order',
          sortOrder: 'asc'
        }
      });

      setPackages(response.data.packages);
      setPagination(response.data.pagination);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch packages');
      console.error('Error fetching packages:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToWorkspaces = () => {
    navigate('/workspaces');
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setPagination({ ...pagination, page: 1 });
  };

  const handleProjectFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setProjectFilter(e.target.value);
    setPagination({ ...pagination, page: 1 });
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    // Reset to first project instead of empty
    if (projects.length > 0) {
      setProjectFilter(projects[0]._id);
    }
    setPagination({ ...pagination, page: 1 });
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = async (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    e.stopPropagation();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    // Reorder packages array
    const newPackages = [...packages];
    const [draggedPackage] = newPackages.splice(draggedIndex, 1);
    newPackages.splice(dropIndex, 0, draggedPackage);

    // Update local state immediately for better UX
    setPackages(newPackages);
    setDraggedIndex(null);
    setDragOverIndex(null);

    // Update order in backend
    try {
      await Promise.all(
        newPackages.map((pkg, index) =>
          api.put(
            `/workspaces/${workspaceId}/pkg-packages/${pkg._id}`,
            { order: index },
          )
        )
      );
    } catch (err: any) {
      console.error('Error updating package order:', err);
      // Revert on error by refetching
      fetchPackages();
    }
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handlePageChange = (page: number) => {
    setPagination({ ...pagination, page });
  };

  const handleCreatePackage = () => {
    setSearchParams({ action: 'new' });
  };

  const handleViewPackage = (id: string) => {
    setSearchParams({ view: id });
  };

  const handleCloseDrawer = () => {
    setSearchParams({});
    fetchPackages();
  };

  const handleCloseDetails = () => {
    setSearchParams({});
    fetchPackages();
  };

  const isDrawerOpen = searchParams.get('action') === 'new';
  const viewPackageId = searchParams.get('view');

  if (!workspaceId) {
    return (
      <AppLayout onBack={handleBackToWorkspaces} backLabel="Workspaces">
        <div className="builders-container">
          <div className="builders-error">
            <p>No workspace selected</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout onBack={handleBackToWorkspaces} backLabel="Workspaces">
      <>
        <div className="builders-container">
          {/* Section Header */}
          <div className="builders-section-header">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h2 className="builders-section-title">Packages</h2>
                <p className="builders-section-subtitle">
                  Manage packages and their configurations
                </p>
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button className="btn btn-outline btn-md" onClick={handleClearFilters}>
                  Clear
                </button>
                <button className="btn btn-primary btn-md" onClick={handleCreatePackage}>
                  + Add New Package
                </button>
              </div>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="builders-error" style={{ marginBottom: '20px' }}>
              <p>{error}</p>
            </div>
          )}
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="builders-container">
            <div className="builders-loading">
              <div className="builders-loading-spinner" />
            </div>
          </div>
        ) : (
          <div style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 2px 8px rgba(72, 24, 47, 0.08)'
          }}>
            {/* Filters */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', marginBottom: '24px' }}>
              <select
                className="form-input"
                value={projectFilter}
                onChange={handleProjectFilterChange}
              >
                {projects.map(project => (
                  <option key={project._id} value={project._id}>{project.name}</option>
                ))}
              </select>

              <input
                type="text"
                className="form-input"
                placeholder="Search packages..."
                value={searchQuery}
                onChange={handleSearchChange}
              />
            </div>

            {/* Packages Table */}
            <div className="data-table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}></th>
                    <th>Image</th>
                    <th>Project</th>
                    <th>Name</th>
                    <th>Collections</th>
                    <th>Location</th>
                    <th>Phase</th>
                    <th>Order</th>
                    <th>Status</th>
                    <th>Options</th>
                    <th>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {packages.length === 0 ? (
                    <tr>
                      <td colSpan={11} className="data-table-empty">
                        No packages found. Click "+ Add New Package" to create one.
                      </td>
                    </tr>
                  ) : (
                    packages.map((pkg, index) => (
                      <tr
                        key={pkg._id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, index)}
                        onDragEnd={handleDragEnd}
                        style={{
                          opacity: draggedIndex === index ? 0.4 : 1,
                          backgroundColor: dragOverIndex === index ? '#F3F4F6' : undefined,
                          cursor: 'move'
                        }}
                      >
                        <td style={{ cursor: 'grab', padding: '8px' }}>
                          <GripVertical size={20} color="#9CA3AF" />
                        </td>
                        <td onClick={() => handleViewPackage(pkg._id)} style={{ cursor: 'pointer' }}>
                          {pkg.heroImageUrl ? (
                            <img
                              src={pkg.heroImageUrl}
                              alt={pkg.name}
                              style={{
                                width: '40px',
                                height: '40px',
                                objectFit: 'cover',
                                borderRadius: '4px',
                                border: '1px solid #E5E7EB'
                              }}
                            />
                          ) : (
                            <div style={{ width: '40px', height: '40px', backgroundColor: '#E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '4px' }}>
                              <PackageIcon size={24} color="#6B7280" />
                            </div>
                          )}
                        </td>
                        <td onClick={() => handleViewPackage(pkg._id)} style={{ cursor: 'pointer' }} className="data-table-primary-cell">{pkg.projectName || '-'}</td>
                        <td onClick={() => handleViewPackage(pkg._id)} style={{ cursor: 'pointer' }} className="data-table-primary-cell">{pkg.name}</td>
                        <td onClick={() => handleViewPackage(pkg._id)} style={{ cursor: 'pointer' }}>
                          {pkg.collections && pkg.collections.length > 0
                            ? pkg.collections.map((c: any) => c.name).join(', ')
                            : '-'}
                        </td>
                        <td onClick={() => handleViewPackage(pkg._id)} style={{ cursor: 'pointer' }}>{pkg.location || '-'}</td>
                        <td onClick={() => handleViewPackage(pkg._id)} style={{ cursor: 'pointer' }}>{pkg.phase || '-'}</td>
                        <td onClick={() => handleViewPackage(pkg._id)} style={{ cursor: 'pointer' }}>{pkg.order}</td>
                        <td onClick={() => handleViewPackage(pkg._id)} style={{ cursor: 'pointer' }}>
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            backgroundColor: pkg.isActive ? '#DEF7EC' : '#FEE2E2',
                            color: pkg.isActive ? '#03543F' : '#991B1B'
                          }}>
                            {pkg.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td onClick={() => handleViewPackage(pkg._id)} style={{ cursor: 'pointer' }}>{pkg.includedOptions?.length || 0}</td>
                        <td onClick={() => handleViewPackage(pkg._id)} style={{ cursor: 'pointer' }}>{new Date(pkg.updatedAt).toLocaleDateString()}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="builders-pagination" style={{ marginTop: '20px' }}>
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="btn btn-secondary btn-sm"
                >
                  Previous
                </button>
                <span>
                  Page {pagination.page} of {pagination.pages}
                </span>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.pages}
                  className="btn btn-secondary btn-sm"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}

        {/* Package Form Drawer (Create Only) */}
        <Drawer
          isOpen={isDrawerOpen}
          onClose={handleCloseDrawer}
          title="Add New Package"
          footer={
            <div className="drawer-footer-actions">
              <button
                type="button"
                className="btn btn-secondary btn-md"
                onClick={handleCloseDrawer}
                disabled={formLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                form="package-form"
                className="btn btn-primary btn-md"
                disabled={formLoading}
              >
                {formLoading ? 'Creating...' : 'Create'}
              </button>
            </div>
          }
        >
          {isDrawerOpen && workspaceId && (
            <PackageFormSlider
              workspaceId={workspaceId}
              projectId={projectFilter || undefined}
              onSuccess={handleCloseDrawer}
              onCancel={handleCloseDrawer}
              onLoadingChange={setFormLoading}
            />
          )}
        </Drawer>

        {/* Package Details Slider (View/Edit) */}
        {viewPackageId && workspaceId && (
          <PackageDetailsSlider
            workspaceId={workspaceId}
            packageId={viewPackageId}
            onClose={handleCloseDetails}
            onSave={fetchPackages}
          />
        )}
      </>
    </AppLayout>
  );
};

export default Packages;
